/* eslint-disable no-useless-escape, no-eval */
import { useState, useRef, useCallback, useEffect, useMemo } from "react";

const AI_URL = "https://white-limit-e2fe.luckyfaizu3.workers.dev/chat";

async function callAI(messages, onChunk) {
  const res = await fetch(AI_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages, max_tokens: 800, temperature: 0.1 }),
  });
  if (!res.ok) throw new Error("AI offline (" + res.status + ")");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let leftover = "", full = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = leftover + dec.decode(value, { stream: true });
    const lines = chunk.split("\n");
    leftover = lines.pop() || "";
    for (const l of lines) {
      const t = l.trim();
      if (!t.startsWith("data: ")) continue;
      const d = t.slice(6).trim();
      if (d === "[DONE]" || !d) continue;
      try {
        const p = JSON.parse(d);
        const delta = p.choices?.[0]?.delta?.content;
        if (delta) { full += delta; onChunk?.(full); }
      } catch (_) {}
    }
  }
  return full.trim();
}

const LANGS = {
  python:     { label: "Python",     short: "PY",  ext: "py",   badge: "PY", color: "#3b82f6", runner: "pyodide" },
  javascript: { label: "JavaScript", short: "JS",  ext: "js",   badge: "JS", color: "#f59e0b", runner: "eval"   },
  typescript: { label: "TypeScript", short: "TS",  ext: "ts",   badge: "TS", color: "#0ea5e9", runner: "ai"     },
  html:       { label: "HTML/CSS",   short: "HTML",ext: "html", badge: "HT", color: "#f97316", runner: "html"   },
  cpp:        { label: "C++",        short: "C++", ext: "cpp",  badge: "C+", color: "#8b5cf6", runner: "ai"     },
  csharp:     { label: "C#",         short: "C#",  ext: "cs",   badge: "C#", color: "#ec4899", runner: "ai"     },
  java:       { label: "Java",       short: "Java",ext: "java", badge: "JV", color: "#ef4444", runner: "ai"     },
};

const TEMPLATES = {
  python: `# Python 3.11
def greet(name):
    return f"Hello, {name}!"

for i in range(1, 6):
    print(f"{i}: {greet('World')}")
`,
  javascript: `// JavaScript
function fibonacci(n) {
  if (n <= 1) return n;
  return fibonacci(n - 1) + fibonacci(n - 2);
}

for (let i = 0; i < 8; i++) {
  console.log(\`fib(\${i}) = \${fibonacci(i)}\`);
}
`,
  typescript: `// TypeScript
interface User {
  name: string;
  age: number;
}

function greet(user: User): string {
  return \`Hello, \${user.name}! Age: \${user.age}\`;
}

const user: User = { name: "Alice", age: 30 };
console.log(greet(user));
`,
  html: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <style>
    body {
      margin: 0;
      font-family: 'Segoe UI', sans-serif;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea, #764ba2);
    }
    .card {
      background: white;
      padding: 40px 60px;
      border-radius: 20px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.2);
      text-align: center;
    }
    h1 { color: #1a1a2e; margin: 0 0 10px; }
    p  { color: #666; margin: 0; }
  </style>
</head>
<body>
  <div class="card">
    <h1>Hello, World!</h1>
    <p>HTML + CSS running live.</p>
  </div>
</body>
</html>`,
  cpp: `#include <iostream>
#include <vector>
using namespace std;

int main() {
    vector<int> nums = {1, 2, 3, 4, 5};
    int sum = 0;
    for (int n : nums) {
        sum += n;
        cout << "num: " << n << endl;
    }
    cout << "Sum = " << sum << endl;
    return 0;
}`,
  csharp: `using System;
using System.Collections.Generic;

class Program {
    static void Main() {
        var fruits = new List<string> { "Apple", "Banana", "Cherry" };
        foreach (var f in fruits) {
            Console.WriteLine($"Fruit: {f}");
        }
        Console.WriteLine($"Total: {fruits.Count}");
    }
}`,
  java: `import java.util.Arrays;
import java.util.List;

public class Main {
    public static void main(String[] args) {
        List<String> langs = Arrays.asList("Java", "Python", "C++");
        for (String lang : langs) {
            System.out.println("Language: " + lang);
        }
        System.out.println("Done!");
    }
}`,
};

const TURTLE_CODE = `import turtle

t = turtle.Turtle()
t.speed(6)
colors = ["#e74c3c","#e67e22","#f1c40f","#2ecc71","#3498db","#9b59b6"]
for i in range(60):
    t.color(colors[i % 6])
    t.forward(i * 3)
    t.right(91)
turtle.done()`;

const hasTurtle = c => /\bimport\s+turtle\b|from\s+turtle\s+import/.test(c);
const hasInput  = c => /\binput\s*\(/.test(c);

const getPrompts = c => {
  const r = [];
  const re = /\binput\s*\(\s*(f?(?:"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'))?\s*\)/g;
  let m;
  while ((m = re.exec(c)) !== null) {
    if (m[1]) {
      const raw = m[1].replace(/^f?["']|["']$/g, "");
      r.push(raw);
    } else {
      r.push("");
    }
  }
  return r;
};

const esc = s => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const PY_KW   = new Set(["def","class","import","from","return","if","elif","else","for","while","in","not","and","or","True","False","None","try","except","finally","with","as","pass","break","continue","lambda","yield","async","await","raise","del","global","nonlocal","assert","is"]);
const PY_BT   = new Set(["print","len","range","type","int","str","float","list","dict","set","tuple","input","enumerate","zip","map","filter","sorted","max","min","sum","abs","round","isinstance","super","bool","repr","format","any","all","open","next","iter"]);
const JS_KW   = new Set(["const","let","var","function","return","if","else","for","while","class","import","export","new","true","false","null","undefined","async","await","typeof","instanceof","of","in","do","switch","case","break","continue","throw","try","catch","finally","from","default","extends","static","this","super"]);
const CS_KW   = new Set(["using","namespace","class","public","private","protected","static","void","int","string","bool","float","double","var","new","return","if","else","for","foreach","while","switch","case","break","continue","try","catch","finally","async","await","throw","interface","enum","abstract","override","null","true","false"]);
const JAVA_KW = new Set(["public","private","protected","static","void","int","String","boolean","float","double","class","new","return","if","else","for","while","switch","case","break","continue","try","catch","finally","import","package","extends","implements","interface","enum","abstract","final","null","true","false","this","super"]);
const CPP_KW  = new Set(["int","float","double","char","bool","void","string","auto","const","static","return","if","else","for","while","do","switch","case","break","continue","class","struct","namespace","using","new","delete","nullptr","true","false","public","private","protected","virtual","override","template","typename","sizeof","enum","cout","cin","endl"]);

function findCommentIndex(line) {
  let inSingle = false, inDouble = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    const prev = line[i - 1];
    if (ch === "'" && !inDouble && prev !== "\\") inSingle = !inSingle;
    if (ch === '"' && !inSingle && prev !== "\\") inDouble = !inDouble;
    if (ch === "#" && !inSingle && !inDouble) return i;
  }
  return -1;
}

function hlCode(lang, code) {
  if (!code) return `<span style="color:#aaa">// Start typing...</span>`;
  const numColor = "#c18401", strColor = "#008000", cmtColor = "#888", kwColor2 = "#7c00d3";

  if (lang === "python") {
    let h = "";
    code.split("\n").forEach((line, li, arr) => {
      const ci = findCommentIndex(line);
      const cp = ci >= 0 ? line.slice(0, ci) : line;
      const cm = ci >= 0 ? line.slice(ci) : "";
      const re = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|\b\d+\.?\d*\b|\b\w+\b|[^\w\s]|\s+)/g;
      let m;
      while ((m = re.exec(cp)) !== null) {
        const w = m[0], e = esc(w);
        if (/^\s+$/.test(w))        { h += e; continue; }
        if (w[0]==='"'||w[0]==="'") { h += `<span style="color:${strColor}">${e}</span>`; continue; }
        if (/^\d/.test(w))          { h += `<span style="color:${numColor}">${e}</span>`; continue; }
        if (PY_KW.has(w))           { h += `<span style="color:${kwColor2};font-weight:700">${e}</span>`; continue; }
        if (PY_BT.has(w))           { h += `<span style="color:#0070c1;font-weight:600">${e}</span>`; continue; }
        h += `<span style="color:#1e1e1e">${e}</span>`;
      }
      if (cm) h += `<span style="color:${cmtColor};font-style:italic">${esc(cm)}</span>`;
      if (li < arr.length - 1) h += "\n";
    });
    return h;
  }

  let kwSet, commentStr, kwColor;
  switch (lang) {
    case "javascript": case "typescript": kwSet = JS_KW;   commentStr = "//"; kwColor = "#7c00d3"; break;
    case "csharp":                        kwSet = CS_KW;   commentStr = "//"; kwColor = "#7c00d3"; break;
    case "java":                          kwSet = JAVA_KW; commentStr = "//"; kwColor = "#7c00d3"; break;
    case "cpp":                           kwSet = CPP_KW;  commentStr = "//"; kwColor = "#7c00d3"; break;
    default: return esc(code);
  }

  let h = "";
  code.split("\n").forEach((line, li, arr) => {
    const ci = line.indexOf(commentStr);
    const cp = ci >= 0 ? line.slice(0, ci) : line;
    const cm = ci >= 0 ? line.slice(ci) : "";
    const re = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`|\b\d+\.?\d*\b|\b\w+\b|[^\w\s]|\s+)/g;
    let m;
    while ((m = re.exec(cp)) !== null) {
      const w = m[0], e = esc(w);
      if (/^\s+$/.test(w))                    { h += e; continue; }
      if (w[0]==='"'||w[0]==="'"||w[0]==='`') { h += `<span style="color:${strColor}">${e}</span>`; continue; }
      if (/^\d/.test(w))                       { h += `<span style="color:${numColor}">${e}</span>`; continue; }
      if (kwSet.has(w))                        { h += `<span style="color:${kwColor};font-weight:700">${e}</span>`; continue; }
      h += `<span style="color:#1e1e1e">${e}</span>`;
    }
    if (cm) h += `<span style="color:${cmtColor};font-style:italic">${esc(cm)}</span>`;
    if (li < arr.length - 1) h += "\n";
  });
  return h;
}

/* ═══════════════════════════════════════════════════
   TURTLE HTML — FIX: Fully responsive canvas, fits mobile screen
═══════════════════════════════════════════════════ */
function makeTurtleHTML(code) {
  const cleaned = code
    .replace(/screen\.bgcolor\s*\([^)]*\)/g, "pass")
    .replace(/turtle\.bgcolor\s*\([^)]*\)/g, "pass")
    .replace(/wn\.bgcolor\s*\([^)]*\)/g, "pass")
    .replace(/t\.screen\.bgcolor\s*\([^)]*\)/g, "pass");

  const escaped = cleaned
    .replace(/\\/g, "\\\\")
    .replace(/`/g, "\\`")
    .replace(/\$/g, "\\$");

  return `<!DOCTYPE html>
<html>
<head>
<meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no"/>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  html, body {
    width:100%; height:100%;
    background:#f2f2f2;
    overflow:hidden;
  }
  body {
    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:flex-start;
    height:100vh;
    font-family:'Segoe UI',sans-serif;
    padding: 8px;
    gap:8px;
  }
  #st {
    color:#2d7a2d; font-size:12px; font-weight:600;
    padding:4px 14px; background:rgba(45,122,45,0.08);
    border-radius:20px; border:1px solid rgba(45,122,45,0.2);
    flex-shrink:0; white-space:nowrap;
  }
  #turtle-canvas-container {
    display:flex;
    align-items:center;
    justify-content:center;
    width:100%;
    flex:1;
    min-height:0;
    overflow:hidden;
  }
  /* Hide extra canvases Skulpt may create */
  #turtle-canvas-container canvas:not(:first-child) {
    display: none !important;
  }
  #turtle-canvas-container canvas {
    border-radius:12px;
    box-shadow:0 4px 20px rgba(0,0,0,0.12);
    background:#ffffff !important;
    /* KEY FIX: constrain canvas to fit within screen */
    max-width: 100% !important;
    max-height: 100% !important;
    width: auto !important;
    height: auto !important;
    display:block;
    object-fit: contain;
  }
  #er {
    color:#cc0000; font-size:12px; max-width:90vw;
    word-break:break-word; text-align:center;
    padding:8px 16px; background:rgba(204,0,0,0.06);
    border-radius:8px; border:1px solid rgba(204,0,0,0.18);
    display:none; flex-shrink:0;
  }
  .loader {
    width:10px; height:10px;
    border:2px solid rgba(45,122,45,0.15);
    border-top-color:#2d7a2d;
    border-radius:50%;
    animation:spin 0.8s linear infinite;
    display:inline-block; vertical-align:middle; margin-right:5px;
  }
  @keyframes spin { to { transform:rotate(360deg); } }
</style>
</head>
<body>
<div id="st"><span class="loader" id="ld"></span>Loading Skulpt...</div>
<div id="turtle-canvas-container"></div>
<div id="er"></div>
<script>
function showErr(msg) {
  var er = document.getElementById('er');
  er.textContent = msg;
  er.style.display = 'block';
  document.getElementById('st').textContent = 'Error';
  document.getElementById('ld').style.display = 'none';
}
function loadScript(src, cb) {
  var s = document.createElement('script');
  s.src = src;
  s.onload = cb;
  s.onerror = function() { showErr('Failed to load: ' + src); };
  document.head.appendChild(s);
}
window.addEventListener('load', function() {
  var SKULPT_MIN = 'https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/skulpt.min.js';
  var SKULPT_STD = 'https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/skulpt-stdlib.js';
  loadScript(SKULPT_MIN, function() {
    loadScript(SKULPT_STD, function() {
      document.getElementById('ld').style.display = 'none';
      document.getElementById('st').textContent = 'Running...';

      var container = document.getElementById('turtle-canvas-container');
      /* FIX: Use actual available space, leave room for status bar + padding */
      var availW = window.innerWidth - 16;
      var availH = window.innerHeight - 60; /* 60 = status bar + padding */
      var sz = Math.min(availW, availH, 500); /* cap at 500 */

      Sk.configure({
        output: function() {},
        read: function(f) {
          if (!Sk.builtinFiles || !Sk.builtinFiles.files[f])
            throw 'File not found: ' + f;
          return Sk.builtinFiles.files[f];
        }
      });
      Sk.TurtleGraphics = {
        target: 'turtle-canvas-container',
        width: sz,
        height: sz
      };
      Sk.misceval.asyncToPromise(function() {
        return Sk.importMainWithBody('<stdin>', false, \`${escaped}\`, true);
      }).then(function() {
        document.getElementById('st').textContent = '✓ Done';
        /* After drawing, scale canvas via CSS if it's still too big */
        var canvas = container.querySelector('canvas');
        if (canvas) {
          canvas.style.maxWidth = availW + 'px';
          canvas.style.maxHeight = availH + 'px';
          canvas.style.width = 'auto';
          canvas.style.height = 'auto';
        }
      }).catch(function(e) {
        showErr(e.toString());
      });
    });
  });
});
<\/script>
</body>
</html>`;
}

/* ═══════════════════════════════════════════════════
   PYODIDE WEB WORKER
═══════════════════════════════════════════════════ */
function createPyWorkerURL() {
  const workerCode = `
importScripts('https://cdn.jsdelivr.net/pyodide/v0.27.0/full/pyodide.js');

let pyodide = null;

async function initPyodide() {
  self.postMessage({ type: 'progress', pct: 10, msg: 'Downloading Python runtime...' });
  pyodide = await loadPyodide({ indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.27.0/full/' });
  self.postMessage({ type: 'progress', pct: 60, msg: 'Loading packages...' });
  try { await pyodide.loadPackage(['micropip']); } catch(_) {}
  self.postMessage({ type: 'progress', pct: 100, msg: 'Ready' });
  self.postMessage({ type: 'ready' });
}

initPyodide().catch(e => {
  self.postMessage({ type: 'error', text: 'Pyodide load failed: ' + e.message });
});

self.onmessage = async function(e) {
  const { code, inputs, id } = e.data;
  if (!pyodide) {
    self.postMessage({ type: 'run_error', id, text: 'Python not ready yet' });
    return;
  }
  try {
    pyodide.runPython(\`
import sys, io, builtins
_out_lines = []
_err_lines = []
class _Writer(io.TextIOBase):
    def __init__(self, buf): self._buf = buf
    def write(self, s):
        if s:
            self._buf.append(str(s))
        return len(s)
    def flush(self): pass
sys.stdout = _Writer(_out_lines)
sys.stderr = _Writer(_err_lines)
\`);

    const BUILTIN = new Set(['sys','io','os','re','math','json','time','random','datetime',
      'collections','itertools','functools','string','pathlib','builtins','abc','copy',
      'typing','enum','hashlib','base64','struct','array','heapq','bisect','gc','inspect',
      'traceback','warnings','logging','csv','sqlite3','glob','tempfile','calendar','pprint',
      'textwrap','ast','unittest','asyncio','subprocess','socket','http','html','xml',
      'email','urllib','zipfile','gzip','shutil','stat','platform','decimal','fractions',
      'statistics','cmath','operator','codecs']);
    const re2 = /^(?:import|from)\\s+([\\w]+)/gm;
    const toInstall = new Set(); let m2;
    while ((m2 = re2.exec(code)) !== null) {
      const p = m2[1]; if (!BUILTIN.has(p)) toInstall.add(p);
    }
    if (toInstall.size > 0) {
      self.postMessage({ type: 'output', id, text: 'Installing: ' + [...toInstall].join(', ') + '...', kind: 'system' });
      try {
        await pyodide.runPythonAsync(
          'import micropip\\nfor _p in ' + JSON.stringify([...toInstall]) +
          ':\\n    try: await micropip.install(_p)\\n    except: pass'
        );
        self.postMessage({ type: 'output', id, text: 'Packages ready ✓', kind: 'system' });
      } catch(_) {}
    }

    if (inputs && inputs.length > 0) {
      pyodide.globals.set('_stdin_data', pyodide.toPy(inputs));
      pyodide.runPython(\`
import sys, io
_sin = list(_stdin_data); _si = 0
class _Reader(io.TextIOBase):
    def readline(self):
        global _si
        if _si < len(_sin):
            v = _sin[_si]; _si += 1; return str(v) + '\\\\n'
        return ''
def _inp(p=''):
    if p: sys.stdout.write(str(p)); sys.stdout.flush()
    return sys.stdin.readline().rstrip('\\\\n')
sys.stdin = _Reader()
builtins.input = _inp
\`);
    }

    const MAX_LINES = 2000;
    let lineCount = 0;
    let truncated = false;

    pyodide.globals.set('_post_line', (line) => {
      if (lineCount >= MAX_LINES) { truncated = true; return; }
      lineCount++;
      self.postMessage({ type: 'output', id, text: String(line), kind: 'output' });
    });

    pyodide.runPython(\`
import sys, io
class _StreamWriter(io.TextIOBase):
    def __init__(self):
        self._buf = ''
    def write(self, s):
        if not s: return 0
        self._buf += s
        while '\\\\n' in self._buf:
            line, self._buf = self._buf.split('\\\\n', 1)
            _post_line(line)
        return len(s)
    def flush(self):
        if self._buf:
            _post_line(self._buf)
            self._buf = ''
sys.stdout = _StreamWriter()
\`);

    let runErr = false, errMsg = '';
    try {
      await pyodide.runPythonAsync(code);
      pyodide.runPython('sys.stdout.flush()');
    } catch(ex) {
      runErr = true;
      errMsg = String(ex).replace(/^PythonError:\\s*/, '').trim();
    }

    const errLines = pyodide.globals.get('_err_lines').toJs();
    if (errLines && errLines.length) {
      self.postMessage({ type: 'output', id, text: errLines.join('').trimEnd(), kind: 'error' });
    }
    if (runErr) {
      self.postMessage({ type: 'output', id, text: errMsg, kind: 'error' });
    }
    if (truncated) {
      self.postMessage({ type: 'output', id, text: '[Output truncated at ' + MAX_LINES + ' lines]', kind: 'system' });
    }
    self.postMessage({ type: 'done', id });
  } catch(ex) {
    self.postMessage({ type: 'output', id, text: 'Error: ' + ex.message, kind: 'error' });
    self.postMessage({ type: 'done', id });
  }
};
`;
  const blob = new Blob([workerCode], { type: 'application/javascript' });
  return URL.createObjectURL(blob);
}

let _workerURL = null;
let _worker = null;
let _workerReady = false;
let _workerReadyCallbacks = [];
let _workerProgress = null;
let _runCallbacks = {};

function getWorker(onProgress) {
  if (!_workerURL) _workerURL = createPyWorkerURL();
  if (!_worker) {
    _worker = new Worker(_workerURL);
    _worker.onmessage = (e) => {
      const msg = e.data;
      if (msg.type === 'progress') {
        _workerProgress?.(msg.pct, msg.msg);
      } else if (msg.type === 'ready') {
        _workerReady = true;
        _workerReadyCallbacks.forEach(cb => cb());
        _workerReadyCallbacks = [];
      } else if (msg.type === 'output') {
        _runCallbacks[msg.id]?.onLine?.(msg.text, msg.kind);
      } else if (msg.type === 'done') {
        _runCallbacks[msg.id]?.onDone?.();
        delete _runCallbacks[msg.id];
      } else if (msg.type === 'error') {
        _runCallbacks[msg.id]?.onLine?.(msg.text, 'error');
        _runCallbacks[msg.id]?.onDone?.();
        delete _runCallbacks[msg.id];
      }
    };
    _worker.onerror = (e) => { console.error('Worker error:', e); };
  }
  _workerProgress = onProgress;
  return _worker;
}

function runPythonInWorker({ code, inputs, onProgress, onLine, onDone }) {
  const worker = getWorker(onProgress);
  const id = Math.random().toString(36).slice(2);
  _runCallbacks[id] = { onLine, onDone };
  const doRun = () => { worker.postMessage({ code, inputs, id }); };
  if (_workerReady) { doRun(); } else { _workerReadyCallbacks.push(doRun); }
  return id;
}

function terminateWorker() {
  if (_worker) {
    _worker.terminate();
    _worker = null; _workerReady = false; _workerURL = null;
    _runCallbacks = {}; _workerProgress = null;
  }
}

/* ═══════════════════════════════════════════════════
   UNDO/REDO HISTORY HOOK
═══════════════════════════════════════════════════ */
function useUndoRedo(initial) {
  const [history, setHistory] = useState([initial]);
  const [index, setIndex]     = useState(0);

  const current = history[index];

  const set = useCallback((newVal) => {
    setHistory(h => {
      const trimmed = h.slice(0, index + 1);
      if (trimmed[trimmed.length - 1] === newVal) return h;
      const next = [...trimmed, newVal];
      return next.length > 200 ? next.slice(next.length - 200) : next;
    });
    setIndex(i => {
      const newIdx = Math.min(i + 1, 200);
      return newIdx;
    });
  }, [index]);

  const undo = useCallback(() => { setIndex(i => Math.max(0, i - 1)); }, []);
  const redo = useCallback(() => { setIndex(i => Math.min(history.length - 1, i + 1)); }, [history.length]);

  const canUndo = index > 0;
  const canRedo = index < history.length - 1;

  const reset = useCallback((val) => { setHistory([val]); setIndex(0); }, []);

  return { current, set, undo, redo, canUndo, canRedo, reset };
}

/* ═══════════════════════════════════════════════════
   CODE EDITOR
═══════════════════════════════════════════════════ */
function CodeEditor({ code, onChange, lang, onUndo, onRedo, canUndo, canRedo }) {
  const taRef = useRef(null);
  const hiRef = useRef(null);
  const lines = (code || "").split("\n");
  const lineCount = lines.length;
  const FS = 14, LH = 22;
  const digits = String(lineCount).length;
  const LNW = Math.max(18, digits * 6 + 6);
  const lnRef = useRef(null);

  const syncScroll = () => {
    if (!taRef.current) return;
    const top = taRef.current.scrollTop;
    const left = taRef.current.scrollLeft;
    if (hiRef.current) { hiRef.current.scrollTop = top; hiRef.current.scrollLeft = left; }
    if (lnRef.current)  { lnRef.current.scrollTop = top; }
  };

  const onKeyDown = (e) => {
    const ta = taRef.current; if (!ta) return;
    if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) { e.preventDefault(); onUndo(); return; }
    if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); onRedo(); return; }
    if ((e.ctrlKey || e.metaKey) && e.key === "a") return;
    if ((e.ctrlKey || e.metaKey) && e.key === "c") return;
    const s = ta.selectionStart, end = ta.selectionEnd, v = code;
    if (e.key === "Backspace" && s === end) {
      const ls = v.lastIndexOf("\n", s - 1) + 1;
      const seg = v.slice(ls, s);
      if (/^ +$/.test(seg) && seg.length % 4 === 0 && seg.length > 0) {
        e.preventDefault();
        onChange(v.slice(0, s - 4) + v.slice(s));
        requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s - 4; });
        return;
      }
    }
    if (e.key === "Tab") {
      e.preventDefault();
      onChange(v.slice(0, s) + "    " + v.slice(end));
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 4; });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const ls = v.lastIndexOf("\n", s - 1) + 1;
      const lead = v.slice(ls, s).match(/^(\s*)/)[1].length;
      const needsIndent = /[:{\(]$/.test(v.slice(ls, s).trimEnd());
      const ind = " ".repeat(lead + (needsIndent ? 4 : 0));
      onChange(v.slice(0, s) + "\n" + ind + v.slice(end));
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 1 + ind.length; });
    }
  };

  const guides = useMemo(() => {
    const result = [];
    lines.forEach((line, li) => {
      if (line.trim() === "") return;
      const spaces = line.match(/^(\s*)/)[1].length;
      const levels = Math.floor(spaces / 4);
      for (let gi = 0; gi < levels; gi++) {
        result.push(
          <div key={`g${li}-${gi}`} style={{
            position: "absolute",
            left: LNW + 12 + gi * 4 * (FS * 0.605),
            top: 12 + li * LH,
            width: 1, height: LH,
            background: "rgba(0,0,0,0.08)",
            pointerEvents: "none", zIndex: 1,
          }} />
        );
      }
    });
    return result;
  }, [code, LNW]); // eslint-disable-line react-hooks/exhaustive-deps

  const shared = {
    position: "absolute", inset: 0,
    padding: `12px 12px 12px ${LNW + 8}px`,
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: FS, lineHeight: LH + "px",
    whiteSpace: "pre", overflow: "hidden", tabSize: 4,
  };

  return (
    <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "#ffffff" }}>
      <div ref={lnRef} style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: LNW,
        background: "#f8f8f8", borderRight: "1px solid #ebebeb",
        fontFamily: "'JetBrains Mono',monospace", fontSize: 9, lineHeight: LH + "px",
        overflowY: "hidden", overflowX: "hidden",
        pointerEvents: "none", zIndex: 3, userSelect: "none",
        paddingTop: 12, paddingBottom: 12,
      }}>
        {Array.from({ length: lineCount }).map((_, i) => (
          <div key={i} style={{ textAlign: "right", paddingRight: 4, color: "#ccc", height: LH }}>{i + 1}</div>
        ))}
      </div>

      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1, overflow: "hidden" }}>
        {guides}
      </div>

      <div ref={hiRef} style={{
        ...shared,
        pointerEvents: "none", zIndex: 2, color: "#1e1e1e", wordBreak: "normal",
        overflow: "hidden",
      }}>
        <pre style={{
          margin: 0, padding: 0,
          fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
          fontSize: FS, lineHeight: LH + "px",
          background: "transparent", whiteSpace: "pre", wordBreak: "normal",
        }} dangerouslySetInnerHTML={{ __html: hlCode(lang, code) }} />
      </div>

      <textarea
        ref={taRef} value={code}
        onChange={e => onChange(e.target.value)}
        onScroll={syncScroll}
        onKeyDown={onKeyDown}
        spellCheck={false} autoComplete="off" autoCorrect="off" autoCapitalize="off"
        style={{
          ...shared,
          background: "transparent", color: "transparent",
          caretColor: "#000000", border: "none", outline: "none", resize: "none",
          zIndex: 4, WebkitTextFillColor: "transparent", wordBreak: "normal",
          overflowY: "scroll", overflowX: "auto",
        }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   OUTPUT PANEL
═══════════════════════════════════════════════════ */
function OutputPanel({ lines, isRunning, onClose, onStop, inputPrompt, onInputSubmit, langLabel }) {
  const bottomRef = useRef(null);
  const [inputVal, setInputVal] = useState("");
  const [termCopied, setTermCopied] = useState(false);

  useEffect(() => {
    setTimeout(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, 30);
  }, [lines]);

  const submit = () => {
    const v = inputVal.trim(); if (!v) return;
    onInputSubmit(v); setInputVal("");
  };

  const copyOutput = async () => {
    const text = lines.map(l => {
      const prefix = l.type === "error" ? "✖ " : l.type === "system" ? "● " : "";
      return prefix + l.text;
    }).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setTermCopied(true);
      setTimeout(() => setTermCopied(false), 1500);
    } catch (_) {}
  };

  const lineStyle = (line) => {
    switch (line.type) {
      case "error":   return { color: "#ff0000" };
      case "system":  return { color: "#888", fontStyle: "italic" };
      case "input":   return { color: "#c18401" };
      case "keyword": return { color: "#7c00d3" };
      case "result":  return { color: "#0070c1" };
      default:        return { color: "#1e1e1e" };
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#ffffff", display: "flex", flexDirection: "column" }}>
      {/* FIX: flex-wrap on mobile so buttons don't overflow */}
      <div style={{
        background: "#f5f5f5", borderBottom: "1px solid #e0e0e0",
        padding: "8px 10px", display: "flex", alignItems: "center",
        gap: 6, flexShrink: 0, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#ff5f56" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#ffbd2e" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#27c93f" }} />
        </div>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#555", flex: 1, minWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {langLabel || "Output"} · Terminal
        </span>
        {isRunning && (
          <button onClick={onStop} style={{
            background: "#ff4444", border: "none", color: "#fff",
            borderRadius: 7, padding: "5px 10px", fontSize: 11,
            cursor: "pointer", fontFamily: "'JetBrains Mono',monospace",
            fontWeight: 700, flexShrink: 0,
          }}>■ Stop</button>
        )}
        <button onClick={copyOutput} style={{
          background: termCopied ? "#f0fdf4" : "#fff",
          border: `1px solid ${termCopied ? "#bbf7d0" : "#ddd"}`,
          color: termCopied ? "#16a34a" : "#555",
          borderRadius: 7, padding: "5px 8px", fontSize: 11,
          cursor: "pointer", flexShrink: 0,
          fontFamily: "'JetBrains Mono',monospace",
        }}>
          {termCopied ? "✓" : "⎘"}
        </button>
        <button onClick={onClose} style={{
          background: "#fff", border: "1px solid #ddd", color: "#555",
          borderRadius: 7, padding: "5px 10px", fontSize: 11, cursor: "pointer", flexShrink: 0,
        }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", background: "#ffffff", userSelect: "text", WebkitUserSelect: "text" }}>
        <div style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: 11,
          color: "#aaa", marginBottom: 8, borderBottom: "1px solid #f0f0f0", paddingBottom: 8,
          userSelect: "none",
        }}>
          {langLabel} · PySkill Compiler
        </div>
        {isRunning && lines.length === 0 && (
          <span style={{ color: "#888", fontFamily: "'JetBrains Mono',monospace", fontSize: 13 }}>▶ Running...</span>
        )}
        {lines.map((line, i) => (
          <div key={i} style={{
            fontFamily: "'JetBrains Mono','Fira Code',monospace",
            fontSize: 13, lineHeight: "1.85",
            ...lineStyle(line),
            whiteSpace: "pre-wrap", wordBreak: "break-all",
            paddingLeft: line.type === "error" ? 10 : 0,
            borderLeft: line.type === "error" ? "2px solid #ff0000" : "none",
            userSelect: "text", WebkitUserSelect: "text",
          }}>
            {line.type === "error" ? "✖ " : line.type === "system" ? "● " : ""}{line.text}
          </div>
        ))}
        {inputPrompt !== null && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
            <span style={{ color: "#c18401", fontFamily: "'JetBrains Mono',monospace", fontSize: 13 }}>
              {inputPrompt || "▶"}
            </span>
            <input
              autoFocus value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submit()}
              style={{
                flex: 1, background: "#f5f5f5", border: "1px solid #ddd",
                color: "#1e1e1e", borderRadius: 7, padding: "7px 12px",
                fontFamily: "'JetBrains Mono',monospace", fontSize: 13, outline: "none",
              }}
            />
            <button onClick={submit} style={{
              background: "#0070c1", border: "none", color: "#fff",
              borderRadius: 7, padding: "7px 12px", fontWeight: 700, cursor: "pointer", fontSize: 13,
            }}>↵</button>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   TURTLE SCREEN — FIX: iframe fills remaining space properly
═══════════════════════════════════════════════════ */
function TurtleScreen({ html, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "#f2f2f2", display: "flex", flexDirection: "column" }}>
      <div style={{
        height: 44, flexShrink: 0,
        background: "#f5f5f5", borderBottom: "1px solid #e0e0e0",
        display: "flex", alignItems: "center", padding: "0 12px",
      }}>
        <span style={{ fontFamily: "sans-serif", fontSize: 13, fontWeight: 700, color: "#1e1e1e" }}>🐢 Turtle Graphics</span>
        <button onClick={onClose} style={{
          marginLeft: "auto", background: "#fff", border: "1px solid #ddd", color: "#555",
          borderRadius: 7, padding: "6px 14px", fontFamily: "sans-serif", fontSize: 12, cursor: "pointer",
        }}>← Back</button>
      </div>
      {/* FIX: iframe takes all remaining height, no overflow */}
      <iframe
        title="turtle"
        srcDoc={html}
        sandbox="allow-scripts"
        style={{
          flex: 1,
          width: "100%",
          height: "calc(100vh - 44px)", /* explicit fallback */
          border: "none",
          background: "#f2f2f2",
          display: "block",
          overflow: "hidden",
        }}
        scrolling="no"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   HTML PREVIEW SCREEN
═══════════════════════════════════════════════════ */
function HTMLScreen({ html, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "#fff", display: "flex", flexDirection: "column" }}>
      <div style={{
        height: 44, flexShrink: 0,
        background: "#f5f5f5", borderBottom: "1px solid #e0e0e0",
        display: "flex", alignItems: "center", padding: "0 12px",
      }}>
        <span style={{ fontFamily: "sans-serif", fontSize: 13, fontWeight: 700, color: "#1e1e1e" }}>🌐 HTML Preview</span>
        <button onClick={onClose} style={{
          marginLeft: "auto", background: "#fff", border: "1px solid #ddd", color: "#555",
          borderRadius: 7, padding: "6px 14px", fontFamily: "sans-serif", fontSize: 12, cursor: "pointer",
        }}>← Back</button>
      </div>
      <iframe title="html-preview" srcDoc={html} style={{ flex: 1, width: "100%", border: "none", display: "block" }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   PYTHON LOAD OVERLAY
═══════════════════════════════════════════════════ */
function PyLoadOverlay({ progress, msg }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "rgba(255,255,255,0.97)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18,
    }}>
      <div style={{
        width: 52, height: 52, borderRadius: 14, background: "#f0f7ff", border: "1px solid #bfdbfe",
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26,
      }}>🐍</div>
      <div style={{ fontFamily: "sans-serif", fontSize: 14, fontWeight: 600, color: "#1e1e1e" }}>
        {msg || "Loading Python..."}
      </div>
      <div style={{ width: 220, background: "#e0e0e0", borderRadius: 10, height: 5, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: progress + "%",
          background: "linear-gradient(90deg,#0070c1,#008000)",
          borderRadius: 10, transition: "width 0.4s ease",
        }} />
      </div>
      <div style={{ fontFamily: "sans-serif", fontSize: 11, color: "#aaa" }}>First time only — then instant ⚡</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN APP — FIX: Mobile responsive top/bottom bars
═══════════════════════════════════════════════════ */
export default function App({ setCurrentPage, initialCode }) {
  const [lang,        setLang]       = useState("python");
  const [screen,      setScreen]     = useState("editor");
  const [langOpen,    setLangOpen]   = useState(false);
  const [termLines,   setTermLines]  = useState([]);
  const [termRunning, setTermRunning]= useState(false);
  const [inputPrompt, setInputPrompt]= useState(null);
  const [turtleHTML,  setTurtleHTML] = useState("");
  const [htmlSrc,     setHtmlSrc]    = useState("");
  const [pyLoading,   setPyLoading]  = useState(false);
  const [pyProgress,  setPyProgress] = useState(0);
  const [pyMsg,       setPyMsg]      = useState("");
  const [hasError,    setHasError]   = useState(false);
  const [lastError,   setLastError]  = useState("");
  const [isFixing,    setIsFixing]   = useState(false);
  const [fixMsg,      setFixMsg]     = useState("");
  const [copyDone,    setCopyDone]   = useState(false);

  const { current: code, set: setCodeHistory, undo, redo, canUndo, canRedo, reset } = useUndoRedo(initialCode || TEMPLATES.python);

  const debounceRef = useRef(null);
  const handleCodeChange = useCallback((val) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setCodeHistory(val); }, 400);
    _pendingCode.current = val;
    _forceUpdate.current?.();
  }, [setCodeHistory]);

  const _pendingCode = useRef(code);
  const _forceRender = useState(0);
  const _forceUpdate = useRef(() => _forceRender[1](x => x + 1));
  const displayCode = _pendingCode.current;

  useEffect(() => {
    _pendingCode.current = code;
    _forceUpdate.current();
  }, [code]);

  useEffect(() => {
    if (initialCode) { reset(initialCode); _pendingCode.current = initialCode; }
  }, [initialCode]); // eslint-disable-line

  const inputResolveRef = useRef(null);
  const lineBufferRef   = useRef([]);
  const flushTimerRef   = useRef(null);

  useEffect(() => {
    getWorker((pct, msg) => {
      setPyProgress(pct); setPyMsg(msg);
      if (pct < 100) setPyLoading(true);
      else setPyLoading(false);
    });
  }, []);

  const flushLines = useCallback(() => {
    if (lineBufferRef.current.length === 0) return;
    const toAdd = lineBufferRef.current.splice(0);
    setTermLines(p => {
      const next = [...p, ...toAdd];
      if (next.length > 3000) return next.slice(next.length - 3000);
      return next;
    });
  }, []);

  const addLine = useCallback((text, type = "output") => {
    lineBufferRef.current.push({ text, type });
    if (type === "error") {
      setHasError(true);
      setLastError(prev => prev ? prev + "\n" + text : text);
    }
    clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flushLines, 50);
  }, [flushLines]);

  const waitForInput = useCallback((prompt) => {
    return new Promise(resolve => { setInputPrompt(prompt); inputResolveRef.current = resolve; });
  }, []);

  const handleInputSubmit = useCallback((val) => {
    setInputPrompt(null);
    addLine(val, "input");
    inputResolveRef.current?.(val);
    inputResolveRef.current = null;
  }, [addLine]);

  const handleStop = useCallback(() => {
    terminateWorker();
    getWorker((pct, msg) => {
      setPyProgress(pct); setPyMsg(msg);
      if (pct < 100) setPyLoading(true);
      else setPyLoading(false);
    });
    addLine("⚠ Execution stopped by user.", "system");
    setTermRunning(false);
  }, [addLine]);

  const switchLang = (k) => {
    setLang(k);
    reset(TEMPLATES[k]);
    _pendingCode.current = TEMPLATES[k];
    setLangOpen(false);
    setHasError(false); setLastError(""); setFixMsg("");
    setScreen("editor"); setTermLines([]);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(displayCode);
      setCopyDone(true); setTimeout(() => setCopyDone(false), 1500);
    } catch (_) {}
  };

  const doFix = useCallback(async () => {
    if (!lastError || isFixing) return;
    setIsFixing(true); setFixMsg("AI is analyzing the error...");
    try {
      const langLabel = LANGS[lang]?.label || lang;
      let reply = "";
      await callAI([
        { role: "system", content: `You are an expert ${langLabel} developer. Fix the buggy code. Return ONLY the corrected code inside a markdown code block (\`\`\`${lang}\n...\n\`\`\`). No explanation, no extra text.` },
        { role: "user",   content: `${langLabel} code with error:\n\`\`\`${lang}\n${displayCode}\n\`\`\`\n\nError message:\n${lastError}\n\nReturn only the fixed code block.` },
      ], (partial) => { reply = partial; });
      const match = reply.match(/```(?:\w+)?\n([\s\S]+?)```/);
      if (match) {
        const fixed = match[1].trim();
        reset(fixed); _pendingCode.current = fixed;
        setHasError(false); setLastError("");
        setFixMsg("✓ Fixed! Run again to verify."); setTimeout(() => setFixMsg(""), 4000);
      } else {
        setFixMsg("Could not auto-fix. Please check manually."); setTimeout(() => setFixMsg(""), 5000);
      }
    } catch (e) {
      setFixMsg("AI unavailable: " + e.message); setTimeout(() => setFixMsg(""), 5000);
    }
    setIsFixing(false);
  }, [lang, displayCode, lastError, isFixing, reset]);

  const runCode = useCallback(async (codeToRun) => {
    const raw = (codeToRun ?? displayCode).trim();
    if (!raw) return;
    const runner = LANGS[lang]?.runner || "pyodide";
    setHasError(false); setLastError(""); setFixMsg("");

    if (runner === "html") { setHtmlSrc(raw); setScreen("html"); return; }
    if (runner === "pyodide" && hasTurtle(raw)) { setTurtleHTML(makeTurtleHTML(raw)); setScreen("turtle"); return; }

    setTermLines([]); lineBufferRef.current = [];
    setTermRunning(true); setScreen("output");

    if (runner === "eval") {
      const logs = [];
      const oL = console.log, oE = console.error, oW = console.warn;
      console.log   = (...a) => logs.push({ t: "output", v: a.map(x => typeof x === "object" ? JSON.stringify(x, null, 2) : String(x)).join(" ") });
      console.error = (...a) => logs.push({ t: "error",  v: a.map(String).join(" ") });
      console.warn  = (...a) => logs.push({ t: "output", v: a.map(String).join(" ") });
      try {
        if (/\bawait\b/.test(raw)) {
          // eslint-disable-next-line no-new-func
          await (new Function("return (async () => { " + raw + " })()")());
        } else {
          eval(raw); // eslint-disable-line no-eval
        }
      } catch (e) { logs.push({ t: "error", v: e.message }); }
      console.log = oL; console.error = oE; console.warn = oW;
      if (logs.length) logs.forEach(l => addLine(l.v, l.t));
      else addLine("Done (no output)", "system");
      setTermRunning(false); return;
    }

    if (runner === "ai") {
      try {
        const langLabel = LANGS[lang]?.label || lang;
        let out = "";
        await callAI([
          { role: "system", content: `Simulate the exact console output of this ${langLabel} program. Return ONLY the raw output lines. No markdown, no explanation.` },
          { role: "user",   content: raw },
        ], (partial) => { out = partial; });
        if (out.trim()) out.trim().split("\n").forEach(l => addLine(l));
        else addLine("Done (no output)", "system");
      } catch (e) { addLine("AI unavailable: " + e.message, "error"); }
      setTermRunning(false); return;
    }

    if (runner === "pyodide") {
      let inputs = [];
      if (hasInput(raw)) {
        const prompts = getPrompts(raw);
        for (const p of prompts) {
          const val = await waitForInput(p);
          inputs.push(val);
        }
        setInputPrompt(null);
      }

      let gotOutput = false;
      runPythonInWorker({
        code: raw, inputs,
        onProgress: (pct, msg) => {
          setPyProgress(pct); setPyMsg(msg);
          setPyLoading(pct < 100);
        },
        onLine: (text, kind) => {
          gotOutput = true;
          text.split("\n").forEach(l => addLine(l, kind));
        },
        onDone: () => {
          clearTimeout(flushTimerRef.current);
          flushLines();
          if (!gotOutput) addLine("Done (no output)", "system");
          setTermRunning(false);
          setPyLoading(false);
        },
      });
    }
  }, [displayCode, lang, addLine, waitForInput, flushLines]);

  const handleBack = () => {
    if (typeof setCurrentPage === "function") setCurrentPage("home");
    else window.history.back();
  };

  const cl = LANGS[lang];
  if (screen === "turtle") return <TurtleScreen html={turtleHTML} onClose={() => setScreen("editor")} />;
  if (screen === "html")   return <HTMLScreen   html={htmlSrc}    onClose={() => setScreen("editor")} />;
  if (screen === "output") return (
    <>
      {pyLoading && <PyLoadOverlay progress={pyProgress} msg={pyMsg} />}
      <OutputPanel
        lines={termLines} isRunning={termRunning}
        onClose={() => { setScreen("editor"); setTermRunning(false); }}
        onStop={handleStop}
        inputPrompt={inputPrompt} onInputSubmit={handleInputSubmit}
        langLabel={cl.label}
      />
    </>
  );

  /* ── EDITOR SCREEN ── */
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100dvh", background: "#ffffff", overflow: "hidden" }}>

      {/* TOP BAR — FIX: compact on mobile, no overflow */}
      <div style={{
        height: 46, background: "#f5f5f5", borderBottom: "1px solid #e0e0e0",
        display: "flex", alignItems: "center", padding: "0 8px", gap: 6, flexShrink: 0,
        overflow: "hidden",
      }}>
        {/* Back button — icon only on mobile */}
        <button onClick={handleBack} style={{
          background: "#fff", border: "1px solid #ddd", color: "#555", borderRadius: 8,
          padding: "5px 8px", fontSize: 12, cursor: "pointer",
          fontFamily: "sans-serif",
          display: "flex", alignItems: "center", gap: 3, flexShrink: 0,
          whiteSpace: "nowrap",
        }}>←</button>

        {/* Logo */}
        <span style={{
          fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 800,
          letterSpacing: "-0.4px", flexShrink: 0, color: "#1e1e1e", whiteSpace: "nowrap",
        }}>
          Py<span style={{ color: "#0070c1" }}>Skill</span>
          <span style={{
            marginLeft: 4, fontSize: 9, fontWeight: 600, color: "#888",
            letterSpacing: "0.5px", textTransform: "uppercase", verticalAlign: "middle",
          }}>IDE</span>
        </span>

        <div style={{ flex: 1 }} />

        {/* Language selector */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button onClick={() => setLangOpen(o => !o)} style={{
            display: "flex", alignItems: "center", gap: 4, background: "#fff", border: "1px solid #ddd",
            borderRadius: 8, padding: "5px 7px", fontFamily: "'JetBrains Mono',monospace",
            fontSize: 11, fontWeight: 700, color: "#333", cursor: "pointer",
          }}>
            <span style={{
              width: 18, height: 18, borderRadius: 4, background: cl.color,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 8, fontWeight: 800, color: "#fff", flexShrink: 0,
            }}>{cl.badge}</span>
            <span>{cl.short}</span>
            <span style={{ fontSize: 8, color: "#aaa" }}>▾</span>
          </button>

          {langOpen && (
            <div style={{
              position: "absolute", right: 0, top: "calc(100% + 4px)",
              background: "#fff", borderRadius: 10, border: "1px solid #e0e0e0",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 100,
              overflow: "hidden", minWidth: 155,
            }}>
              {Object.entries(LANGS).map(([k, v]) => (
                <button key={k} onClick={() => switchLang(k)} style={{
                  width: "100%", padding: "9px 12px", background: lang === k ? "#f5f5f5" : "transparent",
                  border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
                  fontFamily: "sans-serif", fontSize: 13, fontWeight: lang === k ? 700 : 400,
                  color: lang === k ? "#1e1e1e" : "#555", textAlign: "left",
                }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: 5, background: v.color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 8, fontWeight: 800, color: "#fff",
                  }}>{v.badge}</span>
                  {v.label}
                  {lang === k && <span style={{ marginLeft: "auto", color: v.color }}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Run button */}
        <button onClick={() => runCode(displayCode)} className="run-btn-live" style={{
          border: "none", color: "#fff", borderRadius: 9, padding: "7px 14px",
          fontSize: 13, fontWeight: 800, cursor: "pointer",
          fontFamily: "'Space Grotesk', sans-serif",
          display: "flex", alignItems: "center", gap: 4,
          letterSpacing: "0.2px", flexShrink: 0,
          textShadow: "0 1px 3px rgba(0,0,0,0.25)",
          whiteSpace: "nowrap",
        }}>
          <span style={{ fontSize: 9 }}>▶</span> Run
        </button>
      </div>

      {/* AI FIX BANNER */}
      {(hasError || fixMsg) && (
        <div style={{
          background: !hasError && fixMsg ? "#f0fdf4" : "#fff5f5",
          borderBottom: "1px solid " + (!hasError && fixMsg ? "#bbf7d0" : "#fecaca"),
          padding: "6px 12px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0,
          flexWrap: "wrap",
        }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: !hasError && fixMsg ? "#22c55e" : "#ff0000" }} />
          <span style={{ fontSize: 12, fontFamily: "sans-serif", flex: 1, color: !hasError && fixMsg ? "#16a34a" : "#cc0000", minWidth: 60 }}>
            {fixMsg || `${cl.label} error`}
          </span>
          {hasError && !isFixing && (
            <button onClick={doFix} style={{
              background: "#ff0000", border: "none", color: "#fff",
              borderRadius: 7, padding: "4px 10px", fontSize: 11, fontWeight: 700,
              cursor: "pointer", flexShrink: 0,
            }}>🔧 AI Fix</button>
          )}
          {isFixing && (
            <span style={{ fontSize: 11, color: "#0070c1", flexShrink: 0 }}>Fixing...</span>
          )}
          {!hasError && fixMsg && (
            <button onClick={() => setFixMsg("")} style={{ background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: 15, flexShrink: 0 }}>×</button>
          )}
        </div>
      )}

      {/* CODE EDITOR */}
      <CodeEditor
        code={displayCode}
        onChange={handleCodeChange}
        lang={lang}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
      />

      {/* BOTTOM BAR — FIX: Scrollable on very small screens */}
      <div style={{
        height: 44, background: "#f5f5f5", borderTop: "1px solid #e0e0e0",
        display: "flex", alignItems: "center", padding: "0 8px", gap: 5, flexShrink: 0,
        overflowX: "auto", overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
      }}>
        {/* Undo */}
        <button onClick={undo} disabled={!canUndo} title="Undo" style={{
          background: canUndo ? "#fff" : "#fafafa",
          border: `1px solid ${canUndo ? "#ddd" : "#ebebeb"}`,
          color: canUndo ? "#333" : "#ccc",
          borderRadius: 7, padding: "5px 9px", fontSize: 15,
          cursor: canUndo ? "pointer" : "default",
          lineHeight: 1, userSelect: "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          minWidth: 32, flexShrink: 0,
        }}>↩</button>

        {/* Redo */}
        <button onClick={redo} disabled={!canRedo} title="Redo" style={{
          background: canRedo ? "#fff" : "#fafafa",
          border: `1px solid ${canRedo ? "#ddd" : "#ebebeb"}`,
          color: canRedo ? "#333" : "#ccc",
          borderRadius: 7, padding: "5px 9px", fontSize: 15,
          cursor: canRedo ? "pointer" : "default",
          lineHeight: 1, userSelect: "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          minWidth: 32, flexShrink: 0,
        }}>↪</button>

        <div style={{ width: 1, height: 18, background: "#e0e0e0", flexShrink: 0 }} />

        <button onClick={() => { reset(""); _pendingCode.current = ""; setHasError(false); setLastError(""); setFixMsg(""); }} style={{
          background: "#fff", border: "1px solid #ddd", color: "#555",
          borderRadius: 7, padding: "5px 9px", fontSize: 12, cursor: "pointer",
          fontFamily: "sans-serif", flexShrink: 0, whiteSpace: "nowrap",
        }}>Clear</button>

        <button onClick={handleCopy} style={{
          background: copyDone ? "#f0fdf4" : "#fff",
          border: `1px solid ${copyDone ? "#bbf7d0" : "#ddd"}`,
          color: copyDone ? "#16a34a" : "#555",
          borderRadius: 7, padding: "5px 9px", fontSize: 12, cursor: "pointer",
          fontFamily: "sans-serif", flexShrink: 0, whiteSpace: "nowrap",
        }}>
          {copyDone ? "✓" : "Copy"}
        </button>

        {lang === "python" && (
          <button onClick={() => { reset(TURTLE_CODE); _pendingCode.current = TURTLE_CODE; }} style={{
            background: "#fff", border: "1px solid #ddd", color: "#008000",
            borderRadius: 7, padding: "5px 9px", fontSize: 12, cursor: "pointer",
            fontFamily: "sans-serif", flexShrink: 0, whiteSpace: "nowrap",
          }}>🐢 Turtle</button>
        )}

        <div style={{ flex: 1, minWidth: 8 }} />
        <span style={{ fontSize: 10, color: "#ccc", fontFamily: "'JetBrains Mono',monospace", flexShrink: 0 }}>PySkill</span>
      </div>

      {langOpen && <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setLangOpen(false)} />}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: #f5f5f5; }
        ::-webkit-scrollbar-thumb { background: #ddd; border-radius: 4px; }
        textarea { -webkit-tap-highlight-color: transparent; }
        input, textarea { font-size: 16px !important; }
        button:active { opacity: 0.85; transform: scale(0.97); }
        body, html { overflow: hidden; position: fixed; width: 100%; height: 100%; background: #fff; }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }
        @keyframes liveGrad {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .run-btn-live {
          background: linear-gradient(
            270deg,
            #f43f5e, #f97316, #eab308, #22c55e,
            #06b6d4, #6366f1, #a855f7, #ec4899, #f43f5e
          );
          background-size: 400% 400%;
          animation: liveGrad 5s ease infinite;
        }
        .run-btn-live:hover { filter: brightness(1.1); }
        .run-btn-live:active { filter: brightness(0.9); transform: scale(0.97); }
      `}</style>
    </div>
  );
}